import React, { useEffect, useState } from 'react'
import { X, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import MemberSearch from '@/components/matching/MemberSearch'
import { resourceService } from '@/services/resourceService'
import { userSearchService } from '@/services/userSearchService'
import { useToast } from '@/contexts/ToastContext'
import { Resource } from '@/types/resource'
import { User } from '@/types/user'

interface ShareResourceModalProps {
  resource: Resource
  onClose: () => void
  onResourceUpdated: (resource: Resource) => void
}

const ShareResourceModal: React.FC<ShareResourceModalProps> = ({
  resource,
  onClose,
  onResourceUpdated
}) => {
  const { showToast } = useToast()

  const [sharedWithUserIds, setSharedWithUserIds] = useState<string[]>(resource.sharedWithUserIds)
  const [knownUsers, setKnownUsers] = useState<Record<string, User>>({})
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)

  useEffect(() => {
    const missingUserIds = sharedWithUserIds.filter((userId) => !knownUsers[userId])
    if (missingUserIds.length === 0) {
      return
    }

    let cancelled = false
    Promise.allSettled(missingUserIds.map((userId) => userSearchService.getUserById(userId))).then((results) => {
      if (cancelled) {
        return
      }
      setKnownUsers((prev) => {
        const next = { ...prev }
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            next[result.value.id] = result.value
          }
        })
        return next
      })
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedWithUserIds])

  const handleShare = async (user: User) => {
    if (sharedWithUserIds.includes(user.id)) {
      return
    }

    setKnownUsers((prev) => ({ ...prev, [user.id]: user }))

    try {
      setPendingUserId(user.id)
      const updated = await resourceService.shareResource(resource.id, [user.id])
      setSharedWithUserIds(updated.sharedWithUserIds)
      onResourceUpdated(updated)
      showToast({ type: 'success', message: `Shared with ${user.firstName} ${user.lastName}` })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to share resource'
      })
    } finally {
      setPendingUserId(null)
    }
  }

  const handleRevoke = async (userId: string) => {
    try {
      setPendingUserId(userId)
      const updated = await resourceService.unshareResource(resource.id, userId)
      setSharedWithUserIds(updated.sharedWithUserIds)
      onResourceUpdated(updated)
      showToast({ type: 'success', message: 'Access revoked' })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to revoke access'
      })
    } finally {
      setPendingUserId(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share &ldquo;{resource.title}&rdquo;
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Add a person</label>
            <MemberSearch onSelect={handleShare} placeholder="Search mentors and mentees..." />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">People with access</h3>
            {sharedWithUserIds.length === 0 ? (
              <p className="text-sm text-gray-500">Not shared with anyone yet.</p>
            ) : (
              <ul className="space-y-2">
                {sharedWithUserIds.map((userId) => {
                  const user = knownUsers[userId]
                  return (
                    <li
                      key={userId}
                      className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2"
                    >
                      <span className="text-sm text-gray-900">
                        {user ? `${user.firstName} ${user.lastName}` : userId}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRevoke(userId)}
                        disabled={pendingUserId === userId}
                        className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ShareResourceModal
